import React from "react";
import { CheckIcon } from "@heroicons/react/solid";
import {
  DatabaseIcon,
  FolderIcon,
  GlobeAltIcon,
  InformationCircleIcon,
  LockClosedIcon,
} from "@heroicons/react/outline";

import { FormBlock, PageContainer } from "../ui";
import HelpIcon from "./HelpIcon";
import EasyTrans from "../bundel/EasyTrans";

function OptionCard({ icon: Icon, title, description, help, selected, onClick }) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      className={`flex w-full cursor-pointer items-start gap-3 rounded-xl border p-4 text-left transition focus:outline-none focus:ring-2 focus:ring-fairblue-500 ${
        selected
          ? "border-fairblue-500 bg-fairblue-50 ring-1 ring-fairblue-500"
          : "border-gray-200 bg-white hover:border-gray-300"
      }`}
    >
      <span
        className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg ${
          selected ? "bg-fairblue-500 text-white" : "bg-gray-100 text-gray-500"
        }`}
      >
        <Icon className="h-5 w-5" aria-hidden="true" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-1 font-medium text-gray-900">
          {title}
          {help && (
            <span onClick={(e) => e.stopPropagation()}>{help}</span>
          )}
        </p>
        {description && (
          <p className="mt-0.5 text-sm text-gray-500">{description}</p>
        )}
      </div>
      <span
        className={`mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 ${
          selected
            ? "border-fairblue-500 bg-fairblue-500 text-white"
            : "border-gray-300"
        }`}
      >
        {selected && <CheckIcon className="h-3 w-3" aria-hidden="true" />}
      </span>
    </div>
  );
}

const RegisterWorkSave = (props) => {
  const toggleFairregister = () => {
    const checked = !props.saveFileSystem;
    props.onSetSaveFileSystem(checked);
    if (!props.saveIpfsSystem) props.onSetSelfSave(!checked);
  };
  const toggleIpfs = () => {
    const checked = !props.saveIpfsSystem;
    props.onSetSaveIpfsSystem(checked);
    if (!props.saveFileSystem) props.onSetSelfSave(!checked);
  };
  const toggleSelf = () => {
    const checked = !props.saveSelfSave;
    props.onSetSelfSave(checked);
    props.onSetSaveFileSystem(!checked);
    props.onSetSaveIpfsSystem(!checked);
  };

  return (
    <PageContainer>
      <div className="mt-10 sm:mt-0">
        <FormBlock
          title={
            <EasyTrans>RegisterWork.Title.SecurityAndStorageOptions</EasyTrans>
          }
          description={
            <EasyTrans>
              RegisterWork.Title.SecurityAndStorageOptionsCaption
            </EasyTrans>
          }
        >
          <div className="space-y-6">
            {/* metadata is always stored */}
            <div className="flex items-start gap-2 rounded-lg bg-fairblue-50 p-3 text-sm text-fairblue-700">
              <InformationCircleIcon
                className="h-5 w-5 flex-shrink-0"
                aria-hidden="true"
              />
              <span>
                <EasyTrans>RegisterWork.SecurityAndStorage.Description</EasyTrans>
              </span>
            </div>

            <div>
              <h4 className="mb-2 text-sm font-medium text-gray-900">
                <EasyTrans>RegisterWork.SecurityAndStorage.TheCreation</EasyTrans>
                <span className="text-red-600">*</span>
              </h4>
              <div className="space-y-3">
                <OptionCard
                  icon={DatabaseIcon}
                  selected={props.saveFileSystem}
                  onClick={toggleFairregister}
                  title={
                    <EasyTrans>
                      RegisterWork.SecurityAndStorage.SaveInFairregister
                    </EasyTrans>
                  }
                  description={
                    <EasyTrans>
                      RegisterWork.SecurityAndStorage.FairregisterDesc
                    </EasyTrans>
                  }
                  help={
                    <HelpIcon
                      Title="RegisterWork.SecurityAndStorage.SaveInFairregister"
                      TransKey="RegisterWork.SecurityAndStorage.HelpSaveInFairregister"
                    />
                  }
                />
                <OptionCard
                  icon={GlobeAltIcon}
                  selected={props.saveIpfsSystem}
                  onClick={toggleIpfs}
                  title={
                    <EasyTrans>
                      RegisterWork.SecurityAndStorage.SaveInIPFS
                    </EasyTrans>
                  }
                  description={
                    <EasyTrans>
                      RegisterWork.SecurityAndStorage.IpfsDesc
                    </EasyTrans>
                  }
                  help={
                    <HelpIcon
                      Title="RegisterWork.SecurityAndStorage.SaveInIPFS"
                      TransKey="RegisterWork.SecurityAndStorage.HelpSaveInIPFS"
                    />
                  }
                />
                <OptionCard
                  icon={FolderIcon}
                  selected={props.saveSelfSave}
                  onClick={toggleSelf}
                  title={
                    <EasyTrans>RegisterWork.SecurityAndStorage.SaveSelf</EasyTrans>
                  }
                  description={
                    <EasyTrans>
                      RegisterWork.SecurityAndStorage.SelfDesc
                    </EasyTrans>
                  }
                  help={
                    <HelpIcon
                      Title="RegisterWork.SecurityAndStorage.SaveSelf"
                      TransKey="RegisterWork.SecurityAndStorage.HelpSaveSelf"
                    />
                  }
                />
              </div>
              {props.saveSelfSave && (
                <p className="mt-2 text-sm text-red-700">
                  <EasyTrans>
                    RegisterWork.SecurityAndStorage.SaveSelfWarning
                  </EasyTrans>
                </p>
              )}
            </div>

            <div className="border-t border-gray-100 pt-6">
              <h4 className="mb-2 text-sm font-medium text-gray-900">
                <EasyTrans>
                  RegisterWork.SecurityAndStorage.VisibilityTitle
                </EasyTrans>
              </h4>
              <div className="grid gap-3 sm:grid-cols-2">
                <OptionCard
                  icon={GlobeAltIcon}
                  selected={props.visibility}
                  onClick={() => props.onSetVisibility(true)}
                  title={
                    <EasyTrans>RegisterWork.SecurityAndStorage.Public</EasyTrans>
                  }
                  description={
                    <EasyTrans>
                      RegisterWork.SecurityAndStorage.PublicDesc
                    </EasyTrans>
                  }
                />
                <OptionCard
                  icon={LockClosedIcon}
                  selected={!props.visibility}
                  onClick={() => props.onSetVisibility(false)}
                  title={
                    <EasyTrans>RegisterWork.SecurityAndStorage.Private</EasyTrans>
                  }
                  description={
                    <EasyTrans>
                      RegisterWork.SecurityAndStorage.PrivateDesc
                    </EasyTrans>
                  }
                />
              </div>
            </div>
          </div>
        </FormBlock>
      </div>

      <div className="hidden sm:block" aria-hidden="true">
        <div className="py-5">
          <div className="border-t border-gray-200" />
        </div>
      </div>
    </PageContainer>
  );
};

export default RegisterWorkSave;
